import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Logo from "@/components/Logo";
import { Loader2, ShieldCheck, Anchor, Globe2, Sailboat, KeyRound } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1561728130-afd430af0493?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));

function Field({ label, k, f, set, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <Label>{label}{required && <span className="text-[hsl(var(--gold))]"> *</span>}</Label>
      <Input type={type} required={required} value={f[k]} onChange={set(k)} placeholder={placeholder} className="mt-1" data-testid={testid} />
    </div>
  );
}

export default function Join() {
  const { code } = useParams();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState("");
  const [f, setF] = useState({ name: "", email: "", password: "", confirm: "", phone: "", deck_name: "", frigate: "", home_galleon: "", home_frigate: "", home_fc: "", home_gm: "" });
  const [bm, setBm] = useState("");
  const [bd, setBd] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { api.get(`/join/${code}`).then((r) => setInfo(r.data)).catch((e) => setInvalid(errMsg(e))); }, [code]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const guest = info?.type === "guest";

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) { setError("Passwords do not match"); return; }
    setBusy(true); setError("");
    try {
      const { confirm, ...body } = f;
      if (bm && bd) body.birthday = `${bm}-${bd}`;
      const { data } = await api.post(`/join/${code}`, body);
      setUser(data.user);
      toast.success(`Welcome aboard, ${data.user.name.split(" ")[0]} — Alora!`);
      navigate("/dashboard");
    } catch (e2) { setError(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <div className="relative hidden w-5/12 overflow-hidden lg:block">
        <img src={HERO} alt="Galleon at sea" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030A16] via-[#030A16]/70 to-[#030A16]/30" />
        <div className="relative z-10 flex h-full flex-col justify-end p-12">
          <div>
            <img src="/ban-logo.jpg" alt="Brothers Across Nigeria Int'l" className="mb-6 h-32 w-32 rounded-full object-cover shadow-2xl ring-4 ring-[hsl(var(--gold))]/50 animate-float" />
            <p className="label-eyebrow">{guest ? "Visiting Brother Access" : "Member Enrolment"}</p>
            <h1 className="mt-3 font-display text-5xl font-800 leading-tight text-white">{guest ? <>A Brother is <span className="gold-text">Always Welcome</span></> : <>Join the <span className="gold-text">Vessel</span></>}</h1>
            <p className="mt-4 max-w-md text-sm text-slate-300">{guest ? "Recognise yourself to the IOTA Galleon — your deck name, frigate and officers — and step aboard as our honoured guest." : "Your officer has opened the gangway. Complete your details to board the IOTA Galleon and be mustered to your frigate."}</p>
          </div>
        </div>
      </div>

      <div className="sea-bg flex w-full flex-col items-center justify-center overflow-y-auto p-6 lg:w-7/12">
        <div className="w-full max-w-2xl">
          <div className="mb-6 lg:hidden"><Logo /></div>
          {invalid ? (
            <div className="glass rounded-2xl p-8 text-center shadow-2xl" data-testid="join-invalid">
              <KeyRound className="mx-auto h-10 w-10 text-destructive" />
              <h2 className="mt-3 font-display text-2xl font-700">Access code not accepted</h2>
              <p className="mt-2 text-sm text-muted-foreground">{invalid}</p>
              <Link to="/login" className="mt-6 inline-block text-sm text-[hsl(var(--water))] hover:underline" data-testid="join-back-login">Already a member? Sign in</Link>
              <p className="mt-2 text-xs text-muted-foreground">No valid code? <Link to="/signup" className="text-[hsl(var(--gold))] hover:underline" data-testid="join-request-access">Request access</Link></p>
            </div>
          ) : !info ? (
            <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--gold))]" /></div>
          ) : (
            <form onSubmit={submit} className="glass rounded-2xl p-8 shadow-2xl" data-testid="join-form">
              <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="label-eyebrow">Access code {code}</p>
                  <h2 className="mt-1 font-display text-3xl font-700">{guest ? "Visiting Brother Registration" : "Create your Member Account"}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Invited by {info.invited_by}{info.note ? ` — “${info.note}”` : ""}</p>
                </div>
                <Badge className={guest ? "bg-[hsl(var(--water))] text-white" : "bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]"} data-testid="join-type-badge">{guest ? <><Globe2 className="mr-1 h-3 w-3" />Visiting Brother</> : <><Anchor className="mr-1 h-3 w-3" />Galleon Member</>}</Badge>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Full name" k="name" f={f} set={set} required placeholder="e.g. Emeka Obi" testid="join-name-input" />
                <Field label="Deck name" k="deck_name" f={f} set={set} required={guest} placeholder="e.g. Stormrider" testid="join-deck-input" />
                <Field label="Email" k="email" f={f} set={set} type="email" required placeholder="you@example.com" testid="join-email-input" />
                <Field label="Phone (optional)" k="phone" f={f} set={set} placeholder="+234 801 234 5678" testid="join-phone-input" />
                <Field label="Password" k="password" f={f} set={set} type="password" required placeholder="At least 8 characters" testid="join-password-input" />
                <Field label="Confirm password" k="confirm" f={f} set={set} type="password" required placeholder="Repeat password" testid="join-confirm-input" />
                <div>
                  <Label>Birthday month</Label>
                  <Select value={bm} onValueChange={setBm}>
                    <SelectTrigger className="mt-1" data-testid="join-bmonth-select"><SelectValue placeholder="Month" /></SelectTrigger>
                    <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1).padStart(2, "0")}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Birthday day</Label>
                  <Select value={bd} onValueChange={setBd}>
                    <SelectTrigger className="mt-1" data-testid="join-bday-select"><SelectValue placeholder="Day" /></SelectTrigger>
                    <SelectContent>{DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {guest ? (
                <div className="mt-6 rounded-xl border border-[hsl(var(--water))]/30 bg-[hsl(var(--water))]/5 p-4" data-testid="join-guest-section">
                  <p className="flex items-center gap-2 text-sm font-700"><Globe2 className="h-4 w-4 text-[hsl(var(--water))]" />Recognition — your home Galleon</p>
                  <p className="mb-3 text-xs text-muted-foreground">So the IOTA Galleon can recognise you properly.</p>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Your Galleon / branch" k="home_galleon" f={f} set={set} required placeholder="e.g. BAN USA" testid="join-home-galleon-input" />
                    <Field label="Your frigate" k="home_frigate" f={f} set={set} required placeholder="e.g. Liberty" testid="join-home-frigate-input" />
                    <Field label="Your frigate FC" k="home_fc" f={f} set={set} required placeholder="Name of your FC" testid="join-home-fc-input" />
                    <Field label="Your frigate GM" k="home_gm" f={f} set={set} required placeholder="Name of your GM" testid="join-home-gm-input" />
                  </div>
                </div>
              ) : (
                <div className="mt-6">
                  <Label className="flex items-center gap-1.5"><Sailboat className="h-4 w-4 text-[hsl(var(--water))]" />Frigate <span className="text-[hsl(var(--gold))]">*</span></Label>
                  {info.frigate ? (
                    <p className="mt-1.5 rounded-lg border border-[hsl(var(--gold))]/30 bg-[hsl(var(--gold))]/10 px-3 py-2 text-sm" data-testid="join-frigate-fixed">You will be mustered to <strong>{info.frigate}</strong>.</p>
                  ) : (
                    <Select value={f.frigate} onValueChange={(v) => setF((s) => ({ ...s, frigate: v }))}>
                      <SelectTrigger className="mt-1.5" data-testid="join-frigate-select"><SelectValue placeholder="Choose your frigate" /></SelectTrigger>
                      <SelectContent>{info.frigates.map((fr) => <SelectItem key={fr} value={fr}>{fr}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {error && <p className="mt-4 text-sm text-destructive" data-testid="join-error">{error}</p>}
              <Button type="submit" disabled={busy} className="mt-6 w-full bg-[hsl(var(--gold))] font-700 text-[#0A192F] hover:bg-[hsl(var(--gold))]/90" data-testid="join-submit-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : guest ? "Board as Visiting Brother" : "Board the Vessel"}
              </Button>
              <p className="mt-4 text-center text-xs text-muted-foreground">Already enrolled? <Link to="/login" className="text-[hsl(var(--water))] hover:underline" data-testid="join-login-link">Sign in</Link></p>
            </form>
          )}
          <p className="mt-6 flex items-center justify-center gap-2 text-center text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5" /> Confidential system. Access by invitation only.</p>
        </div>
      </div>
    </div>
  );
}
